// Z102 PR1: client side of the BoxTop account-code check. All verdicts are computed by the sync-to-boxtrax
// edge function (one name matcher, one classifier); this file only types and transports them.
import { supabase } from '../api/supabase'

export type AccountCheckReason = 'ok' | 'no_account' | 'blank_shell' | 'not_found' | 'unauthorised' | 'bad_response' | 'network'

export interface AccountCheck {
  ok: boolean
  reason: AccountCheckReason
  httpStatus: number | null
  sentAccountNumber: string | null
  boxtopCompanyName: string | null
  addressCount: number | null
  message: string
}

/** Staff already confirmed "same company despite different names" for this BoxTop account. */
export interface NameConfirmed { by: string | null; at: string | null; boxtopName: string | null }

export interface NameMatch {
  status: 'same' | 'similar' | 'different'
  scorePct: number
  ourName: string
  boxtopName: string | null
}

/** What the customers row remembers about the last live check (migration 20261015_z102_boxtop_account_check.sql). */
export interface SavedAccountCheck {
  status: 'ok' | 'failed' | null
  checkedNumber: string | null
  checkedAt: string | null
  checkedBy: string | null
  companyName: string | null
  addressCount: number | null
}

export interface Step1Data {
  customerId: string
  customerName: string
  customerCode: string | null
  accountNumber: string | null
  source: { table: string; column: string; recordId: string; lastChange: { at: string; by: string | null } | null }
  saved: SavedAccountCheck
  nameConfirmed: NameConfirmed | null
  check: AccountCheck
  nameMatch: NameMatch | null
}

export interface AccountActionResponse {
  success: boolean
  error?: string
  reason?: 'not_verified' | 'would_overwrite' | 'save_failed'
  check?: AccountCheck
  nameMatch?: NameMatch | null
  nameConfirmed?: NameConfirmed | null
  current?: string | null
  proposed?: string | null
  willOverwrite?: boolean
  ourName?: string
  ourCode?: string | null
  saved?: boolean
  replaced?: string | null
}

export async function callAccountAction(
  action: 'verify_boxtop_account' | 'bind_boxtop_account',
  body: { customer_id: string; account_number?: string; confirm_overwrite?: boolean; confirm_name_mismatch?: boolean },
): Promise<AccountActionResponse> {
  try {
    const { data: sessionData } = await supabase.auth.getSession()
    const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-to-boxtrax`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionData?.session?.access_token || ''}` },
      body: JSON.stringify({ action, ...body }),
    })
    const json = await resp.json().catch(() => null)
    if (!json) return { success: false, error: `Unreadable response from the server (HTTP ${resp.status})` }
    return json as AccountActionResponse
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Request failed' }
  }
}

/** The status line shown wherever the code appears. "Verified" only while the checked number IS the current number. */
export function accountStatusLabel(accountNumber: string | null, saved: SavedAccountCheck | null | undefined):
  { tone: 'none' | 'ok' | 'failed' | 'unverified'; text: string } {
  if (!accountNumber) return { tone: 'none', text: 'Not set' }
  const same = !!saved && saved.checkedNumber === accountNumber
  const when = saved?.checkedAt ? new Date(saved.checkedAt).toISOString().slice(0, 10) : null
  if (same && saved?.status === 'ok') return { tone: 'ok', text: `Verified${when ? ` · ${when}` : ''}` }
  if (same && saved?.status === 'failed') return { tone: 'failed', text: `Check failed${when ? ` · ${when}` : ''}` }
  return { tone: 'unverified', text: 'Not verified yet' }
}
